/**
 * Staff-only diagnostic for triggers.
 *
 * Paste a trigger UUID + claim UUID, click Diagnose, see exactly which
 * step in the dispatch path would succeed, skip, or fail - and why.
 *
 * Trigger-type-agnostic: the response carries a ``type`` discriminator.
 * Webhook triggers will plug in here with a different ``would_dispatch``
 * shape but the same ``steps`` rendering.
 */

import { useState } from 'react'
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from 'lucide-react'

import api from '@/admin/lib/api'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { StaffShell } from '@/admin/components/staff/staff-shell'

interface DiagnoseStep {
  name: string
  ok: boolean
  detail: unknown
}

interface DiagnoseReport {
  type: 'email' | 'webhook'
  tenant: {
    trigger_tenant: { slug: string; name: string } | null
    claim_tenant: { slug: string; name: string } | null
    cross_tenant_mismatch: boolean
  }
  trigger: {
    uuid: string
    tenant_slug: string
    kind: string
    key: string
    is_active: boolean
    template_slug: string | null
    predicate: unknown
    recipients: unknown
    priority: number
  }
  claim: {
    uuid: string
    tenant_slug: string
    schema: string | null
    holding_uuid: string | null
  }
  verdict: 'would_send' | 'would_skip' | 'would_fail'
  steps: DiagnoseStep[]
  would_dispatch: {
    template_slug: string
    template_kind: string
    template_name: string
    recipient_count: number
    would_mint_upload_session: boolean
  } | null
}

export function StaffTriggerDiagnosePage() {
  const [triggerUuid, setTriggerUuid] = useState('')
  const [claimUuid, setClaimUuid] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [report, setReport] = useState<DiagnoseReport | null>(null)

  async function diagnose() {
    setError(null)
    setReport(null)
    if (!triggerUuid.trim() || !claimUuid.trim()) {
      setError('Both UUIDs are required.')
      return
    }
    setLoading(true)
    try {
      const res = await api.post('/staff/triggers/diagnose/', {
        trigger_uuid: triggerUuid.trim(),
        claim_uuid: claimUuid.trim(),
      })
      setReport(res.data as DiagnoseReport)
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { message?: string; error?: string } } })
        ?.response?.data
      const msg =
        data?.message ?? data?.error ?? 'Failed to diagnose - check the UUIDs and try again.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <StaffShell>
      <div className="mx-auto max-w-3xl space-y-6 p-6">
        <div>
          <h1 className="font-display text-[28px] leading-tight text-[var(--ink)]">
            Trigger diagnostics
          </h1>
          <p className="mt-0.5 text-[12px] text-[var(--ink-3)]">
            Dry-run a communications trigger against a claim. No email is sent.
          </p>
        </div>
        <section className="space-y-3 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-5">
          <div>
            <h2 className="text-[15px] font-medium text-[var(--ink)]">
              Dry-run a trigger against a claim
            </h2>
            <p className="mt-1 text-[12px] text-[var(--ink-3)]">
              Replays the full dispatch path without sending an email. Shows
              tenant identity, recipient resolution, predicate evaluation, and
              what would dispatch.
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="trigger-uuid">Trigger UUID</Label>
              <Input
                id="trigger-uuid"
                value={triggerUuid}
                onChange={(e) => setTriggerUuid(e.target.value)}
                placeholder="e.g. 6fb1b950-b601-448e-a2e9-2efd7901799f"
                className="mt-1 font-mono text-[12px]"
              />
              <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                Where to get it: open any trigger in the admin UI at{' '}
                <span className="font-mono">/settings/email-triggers/&lt;uuid&gt;</span>
                {' '}- the UUID is in the URL. Or in Django admin →{' '}
                <span className="font-mono">communications &gt; Email triggers</span>
                {' '}→ open a row → copy the <span className="font-mono">uuid</span>
                {' '}field. Table:{' '}
                <span className="font-mono">communications_emailtrigger</span>.
              </p>
            </div>
            <div>
              <Label htmlFor="claim-uuid">Claim UUID</Label>
              <Input
                id="claim-uuid"
                value={claimUuid}
                onChange={(e) => setClaimUuid(e.target.value)}
                placeholder="e.g. 806d43dd-4ae4-482d-b3d5-6ed5bbd124ff"
                className="mt-1 font-mono text-[12px]"
              />
              <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                Where to get it: open any claim in the admin UI at{' '}
                <span className="font-mono">/claims/&lt;uuid&gt;</span> - the UUID
                is in the URL. Or in Django admin →{' '}
                <span className="font-mono">claims &gt; Claims</span> → open a row →
                copy the <span className="font-mono">uuid</span> field. Table:{' '}
                <span className="font-mono">claims_claim</span>.
              </p>
            </div>
            <Button type="button" onClick={diagnose} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  Diagnosing…
                </>
              ) : (
                'Diagnose'
              )}
            </Button>
          </div>

          {error ? (
            <p className="text-[12px] text-[var(--status-error)]">{error}</p>
          ) : null}
        </section>

        {report ? <ReportPanel report={report} /> : null}
      </div>
    </StaffShell>
  )
}

// ---------------------------------------------------------------------------

function ReportPanel({ report }: { report: DiagnoseReport }) {
  return (
    <section className="space-y-5 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-5">
      <VerdictHeader report={report} />
      <TenantBlock tenant={report.tenant} />
      <StepsBlock steps={report.steps} />
      {report.would_dispatch ? (
        <WouldDispatchBlock w={report.would_dispatch} />
      ) : null}
    </section>
  )
}

function VerdictHeader({ report }: { report: DiagnoseReport }) {
  const styles = {
    would_send: {
      icon: <CheckCircle2 className="h-4 w-4 text-[var(--status-ok)]" />,
      text: 'Would send',
      bg: 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]',
    },
    would_skip: {
      icon: <AlertTriangle className="h-4 w-4 text-[var(--status-warn)]" />,
      text: 'Would skip',
      bg: 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]',
    },
    would_fail: {
      icon: <XCircle className="h-4 w-4 text-[var(--status-error)]" />,
      text: 'Would fail',
      bg: 'bg-[var(--status-error-bg)] text-[var(--status-error)]',
    },
  }
  const s = styles[report.verdict]
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-[6px] px-3 py-1.5 text-[12px] font-medium ${s.bg}`}
    >
      {s.icon}
      <span>{s.text}</span>
      <span className="text-[11px] opacity-70">· {report.type} trigger</span>
    </div>
  )
}

function TenantBlock({ tenant }: { tenant: DiagnoseReport['tenant'] }) {
  return (
    <div className="space-y-1">
      <h3 className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
        Tenant
      </h3>
      <div className="space-y-1 text-[12px]">
        <div>
          <span className="text-[var(--ink-3)]">Trigger tenant: </span>
          <span className="font-medium text-[var(--ink)]">
            {tenant.trigger_tenant
              ? `${tenant.trigger_tenant.name} (${tenant.trigger_tenant.slug})`
              : '-'}
          </span>
        </div>
        <div>
          <span className="text-[var(--ink-3)]">Claim tenant: </span>
          <span className="font-medium text-[var(--ink)]">
            {tenant.claim_tenant
              ? `${tenant.claim_tenant.name} (${tenant.claim_tenant.slug})`
              : '-'}
          </span>
        </div>
        {tenant.cross_tenant_mismatch ? (
          <div className="mt-2 rounded-[6px] bg-[var(--status-error-bg)] px-3 py-2 text-[11px] text-[var(--status-error)]">
            ⚠ Cross-tenant mismatch - trigger and claim belong to different
            tenants. This almost always means the wrong trigger was chosen.
          </div>
        ) : null}
      </div>
    </div>
  )
}

function StepsBlock({ steps }: { steps: DiagnoseStep[] }) {
  return (
    <div className="space-y-1">
      <h3 className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
        Steps
      </h3>
      <ul className="divide-y divide-[var(--rule)]">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3 py-2">
            {s.ok ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--status-ok)]" />
            ) : (
              <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--status-error)]" />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-medium text-[var(--ink)]">
                {s.name}
              </div>
              <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words rounded-[4px] bg-[var(--bg-light)] p-2 text-[11px] text-[var(--ink-2)]">
                {typeof s.detail === 'string'
                  ? s.detail
                  : JSON.stringify(s.detail, null, 2)}
              </pre>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function WouldDispatchBlock({ w }: { w: NonNullable<DiagnoseReport['would_dispatch']> }) {
  return (
    <div className="space-y-1">
      <h3 className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
        Would dispatch
      </h3>
      <div className="space-y-1 rounded-[6px] bg-[var(--bg-light)] p-3 text-[12px]">
        <div>
          <span className="text-[var(--ink-3)]">Template: </span>
          <span className="font-medium text-[var(--ink)]">
            {w.template_name} ({w.template_slug})
          </span>
        </div>
        <div>
          <span className="text-[var(--ink-3)]">Kind: </span>
          <span className="font-mono text-[11px] text-[var(--ink-2)]">
            {w.template_kind}
          </span>
        </div>
        <div>
          <span className="text-[var(--ink-3)]">Recipients: </span>
          <span className="font-medium text-[var(--ink)]">
            {w.recipient_count}
          </span>
        </div>
        <div>
          <span className="text-[var(--ink-3)]">Would mint upload session: </span>
          <span className="font-medium text-[var(--ink)]">
            {w.would_mint_upload_session ? 'yes' : 'no'}
          </span>
        </div>
      </div>
    </div>
  )
}
